// Pure, DOM-free recognition core. Shared verbatim between the web app
// (a browser canvas adapter feeds it pixel buffers) and the scraper eval
// harness (sharp feeds it pixel buffers). MUST NOT import anything from the
// DOM or Node — only ./phash, which is pure as well.
//
// Pipeline (coarse-to-fine):
//   1. tightenBBox      — trim low-gradient background margins inside the crop
//   2. cropResample     — extract the tightened region at WORK resolution
//   3. recall (pHash)   — union of top-K over a few probe rotations  → candidates
//   4. verifyScore      — ZNCC of gradient maps with a small rotation/scale/shift
//                         search against the *real* sprite → robust re-rank + score
//
// Why this is robust to the field conditions (monitor colour, room/blood
// background, tilt, loose framing, noise):
//   - gradient (Sobel) maps drop colour & flat background, keep silhouette;
//   - ZNCC is invariant to any linear brightness/contrast change (monitor/camera);
//   - the rotation/scale/shift search absorbs tilt and imprecise framing;
//   - tightenBBox removes most of the surrounding scene before matching.

export type Gray = Float32Array; // length = w*h, values 0..255

// ---------------------------------------------------------------------------
// resampling
// ---------------------------------------------------------------------------

// Bilinear sample at fractional (x,y). Coordinates a touch outside the grid are
// clamped to the edge; anything further out returns `oob`.
function sampleBilinear(src: Gray, w: number, h: number, x: number, y: number, oob: number): number {
  if (x < -0.5 || y < -0.5 || x > w - 0.5 || y > h - 0.5) return oob;
  if (x < 0) x = 0; else if (x > w - 1) x = w - 1;
  if (y < 0) y = 0; else if (y > h - 1) y = h - 1;
  const x0 = Math.floor(x), y0 = Math.floor(y);
  const x1 = x0 + 1 < w ? x0 + 1 : x0;
  const y1 = y0 + 1 < h ? y0 + 1 : y0;
  const fx = x - x0, fy = y - y0;
  const a = src[y0 * w + x0]!, b = src[y0 * w + x1]!;
  const c = src[y1 * w + x0]!, d = src[y1 * w + x1]!;
  return a * (1 - fx) * (1 - fy) + b * fx * (1 - fy) + c * (1 - fx) * fy + d * fx * fy;
}

// Resample sub-rectangle (sx,sy,sw,sh) of src into outN×outN (bilinear).
export function cropResample(
  src: Gray, w: number, h: number,
  sx: number, sy: number, sw: number, sh: number,
  outN: number,
): Gray {
  const out = new Float32Array(outN * outN);
  for (let oy = 0; oy < outN; oy++) {
    const syf = sy + ((oy + 0.5) / outN) * sh - 0.5;
    for (let ox = 0; ox < outN; ox++) {
      const sxf = sx + ((ox + 0.5) / outN) * sw - 0.5;
      out[oy * outN + ox] = sampleBilinear(src, w, h, sxf, syf, 0);
    }
  }
  return out;
}

// Warp a square n×n buffer onto itself: rotation (deg), uniform scale, and
// translation (dx,dy px) about the centre. Inverse-mapped, bilinear.
export function affineSquare(src: Gray, n: number, deg: number, scale: number, dx: number, dy: number): Gray {
  const out = new Float32Array(n * n);
  const c = (n - 1) / 2;
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad) / scale, sin = Math.sin(rad) / scale;
  for (let oy = 0; oy < n; oy++) {
    const ry = oy - c - dy;
    for (let ox = 0; ox < n; ox++) {
      const rx = ox - c - dx;
      const sx = c + (cos * rx + sin * ry);
      const sy = c + (-sin * rx + cos * ry);
      out[oy * n + ox] = sampleBilinear(src, n, n, sx, sy, 0);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// features
// ---------------------------------------------------------------------------

// Sobel gradient magnitude. Replicate-edge borders.
export function sobelMag(g: Gray, w: number, h: number): Gray {
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    const y0 = y > 0 ? y - 1 : 0, y1 = y < h - 1 ? y + 1 : h - 1;
    for (let x = 0; x < w; x++) {
      const x0 = x > 0 ? x - 1 : 0, x1 = x < w - 1 ? x + 1 : w - 1;
      const tl = g[y0 * w + x0]!, tc = g[y0 * w + x]!, tr = g[y0 * w + x1]!;
      const ml = g[y * w + x0]!, mr = g[y * w + x1]!;
      const bl = g[y1 * w + x0]!, bc = g[y1 * w + x]!, br = g[y1 * w + x1]!;
      const gx = tr + 2 * mr + br - (tl + 2 * ml + bl);
      const gy = bl + 2 * bc + br - (tl + 2 * tc + tr);
      out[y * w + x] = Math.sqrt(gx * gx + gy * gy);
    }
  }
  return out;
}

// Zero-mean normalised cross-correlation of two equal-length buffers → [-1,1].
// Internally subtracts means, so it is invariant to any linear brightness shift.
export function zncc(a: Gray, b: Gray): number {
  const n = a.length;
  let ma = 0, mb = 0;
  for (let i = 0; i < n; i++) { ma += a[i]!; mb += b[i]!; }
  ma /= n; mb /= n;
  let num = 0, da = 0, db = 0;
  for (let i = 0; i < n; i++) {
    const x = a[i]! - ma, y = b[i]! - mb;
    num += x * y; da += x * x; db += y * y;
  }
  const den = Math.sqrt(da * db);
  return den < 1e-9 ? 0 : num / den;
}

// ZNCC restricted to pixels where `mask` > 0 (the reference silhouette). This
// is what makes matching background-agnostic: the probe's room/blood/pedestal
// pixels fall outside the item's silhouette and never enter the correlation.
export function znccMasked(a: Gray, b: Gray, mask: Gray): number {
  let w = 0, ma = 0, mb = 0;
  const n = a.length;
  for (let i = 0; i < n; i++) {
    const m = mask[i]!;
    if (m <= 0) continue;
    w += m; ma += m * a[i]!; mb += m * b[i]!;
  }
  if (w < 1e-6) return 0;
  ma /= w; mb /= w;
  let num = 0, da = 0, db = 0;
  for (let i = 0; i < n; i++) {
    const m = mask[i]!;
    if (m <= 0) continue;
    const x = a[i]! - ma, y = b[i]! - mb;
    num += m * x * y; da += m * x * x; db += m * y * y;
  }
  const den = Math.sqrt(da * db);
  return den < 1e-9 ? 0 : num / den;
}

// ---------------------------------------------------------------------------
// background trimming
// ---------------------------------------------------------------------------

// Locate the item inside the crop by finding the contiguous high-gradient band
// on each axis, then square the result up around its centre. Robust to a
// textured but roughly uniform background (floor/pedestal/noise) because the
// item's edges raise the projected gradient well above the background floor.
// Returns a sub-rect of the input in input pixel coordinates.
export function tightenBBox(
  gray: Gray, w: number, h: number,
  thresh = 0.32, pad = 0.14,
): { x: number; y: number; w: number; h: number } {
  const mag = sobelMag(gray, w, h);
  const colE = new Float64Array(w), rowE = new Float64Array(h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const m = mag[y * w + x]!;
      colE[x]! += m; rowE[y]! += m;
    }
  }
  // smooth a 1-D projection and find the widest run above min+thresh*(max-min)
  const band = (e: Float64Array, n: number): [number, number] => {
    const s = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      let acc = 0, cnt = 0;
      for (let j = Math.max(0, i - 1); j <= Math.min(n - 1, i + 1); j++) { acc += e[j]!; cnt++; }
      s[i] = acc / cnt;
    }
    let mn = Infinity, mx = -Infinity;
    for (let i = 0; i < n; i++) { if (s[i]! < mn) mn = s[i]!; if (s[i]! > mx) mx = s[i]!; }
    if (mx - mn < 1e-6) return [0, n - 1];
    const t = mn + thresh * (mx - mn);
    let lo = 0; while (lo < n && s[lo]! < t) lo++;
    let hi = n - 1; while (hi > lo && s[hi]! < t) hi--;
    if (hi <= lo) return [0, n - 1];
    return [lo, hi];
  };
  const [x0, x1] = band(colE, w);
  const [y0, y1] = band(rowE, h);
  const bw = x1 - x0 + 1, bh = y1 - y0 + 1;
  const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
  let side = Math.max(bw, bh) * (1 + pad);
  side = Math.min(side, w, h);
  let rx = cx - side / 2, ry = cy - side / 2;
  rx = Math.max(0, Math.min(w - side, rx));
  ry = Math.max(0, Math.min(h - side, ry));
  return { x: rx, y: ry, w: side, h: side };
}

// ---------------------------------------------------------------------------
// recall (pHash) + verify (ZNCC)
// ---------------------------------------------------------------------------

export const RECALL_WORK = 28;

// Channel weights for combining shape (gradient + intensity) and colour.
const W_GRAD = 0.45, W_GRAY = 0.20, W_COLOR = 0.35;

export interface RefEntry {
  hash: Uint8Array; // 8-byte DCT pHash of the clean sprite (kept for tooling)
  gray: Gray;       // WORK×WORK grayscale
  mag: Gray;        // WORK×WORK gradient-magnitude map
  mask: Gray;       // WORK×WORK silhouette weight, dilated (boundary edge included)
  maskCore: Gray;   // WORK×WORK silhouette, eroded (interior only — for colour)
  r: Gray; g: Gray; b: Gray; // WORK×WORK colour planes (for monitor-robust colour ZNCC)
  magLo: Gray;      // RECALL_WORK×RECALL_WORK gradient map (recall stage)
  maskLo: Gray;     // RECALL_WORK×RECALL_WORK silhouette
}

export interface RecogOptions {
  work?: number;         // working resolution for verify (default 48)
  recallK?: number;      // candidates kept after coarse recall (default 48)
  coarseDegs?: number[];
  coarseScales?: number[];
}

export interface RGB { r: Gray; g: Gray; b: Gray; }

export function rgbToGray(c: RGB): Gray {
  const out = new Float32Array(c.r.length);
  for (let i = 0; i < out.length; i++) out[i] = 0.2126 * c.r[i]! + 0.7152 * c.g[i]! + 0.0722 * c.b[i]!;
  return out;
}

function resampleTo(src: Gray, work: number): Gray {
  return work === 32 ? src : cropResample(src, 32, 32, 0, 0, 32, 32, work);
}

// Build a reference entry from its clean 32×32 RGBA sprite. Transparent pixels
// fold to 0; the alpha channel gives the silhouette mask. Produces the WORK-res
// grayscale, gradient map, colour planes, dilated mask, and a low-res pair for
// the recall stage.
export function buildRef(
  rgba32: Uint8Array | Uint8ClampedArray,
  hash: Uint8Array,
  work: number,
): RefEntry {
  const gf = new Float32Array(1024), af = new Float32Array(1024);
  const rf = new Float32Array(1024), gff = new Float32Array(1024), bf = new Float32Array(1024);
  for (let i = 0; i < 1024; i++) {
    const a = rgba32[i * 4 + 3]!;
    const R = rgba32[i * 4]!, G = rgba32[i * 4 + 1]!, B = rgba32[i * 4 + 2]!;
    af[i] = a > 8 ? 255 : 0;
    rf[i] = a > 0 ? R : 0; gff[i] = a > 0 ? G : 0; bf[i] = a > 0 ? B : 0;
    gf[i] = a === 0 ? 0 : 0.2126 * R + 0.7152 * G + 0.0722 * B;
  }
  const g = resampleTo(gf, work);
  const a = resampleTo(af, work);
  const mag = sobelMag(g, work, work);
  // Two masks from the silhouette:
  //   mask     — dilated by 1px, so the all-important boundary edge is kept (shape)
  //   maskCore — eroded by 1px, interior only, so colour never samples the
  //              item↔background boundary where probe colours are contaminated.
  const mask = new Float32Array(work * work);
  const maskCore = new Float32Array(work * work);
  const inside = (xx: number, yy: number) =>
    xx >= 0 && yy >= 0 && xx < work && yy < work && a[yy * work + xx]! > 64;
  for (let y = 0; y < work; y++) for (let x = 0; x < work; x++) {
    let anyOn = false, allOn = a[y * work + x]! > 64;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue;
      const on = inside(x + dx, y + dy);
      anyOn = anyOn || on || (a[y * work + x]! > 64);
      if (!on) allOn = false;
    }
    mask[y * work + x] = anyOn ? 1 : 0;
    maskCore[y * work + x] = allOn ? 1 : 0;
  }
  const gLo = resampleTo(gf, RECALL_WORK);
  const aLo = resampleTo(af, RECALL_WORK);
  const magLo = sobelMag(gLo, RECALL_WORK, RECALL_WORK);
  const maskLo = new Float32Array(RECALL_WORK * RECALL_WORK);
  for (let i = 0; i < maskLo.length; i++) maskLo[i] = aLo[i]! > 40 ? 1 : 0;
  return {
    hash, gray: g, mag, mask, maskCore,
    r: resampleTo(rf, work), g: resampleTo(gff, work), b: resampleTo(bf, work),
    magLo, maskLo,
  };
}

export interface ScoredCandidate { idx: number; score: number; recallScore: number; }

// Coarse-to-fine masked verification of the probe against one reference.
// Alignment is driven by shape (gradient ZNCC); the returned score blends
// shape (gradient + intensity) with colour (per-channel ZNCC). Every channel is
// zero-mean normalised over the silhouette, so the score is invariant to any
// per-channel linear gain/offset — i.e. monitor colour cast and white balance.
function verifyOne(
  probe: RGB & { gray: Gray; mag: Gray }, work: number, ref: RefEntry,
  coarseDegs: number[], coarseScales: number[],
): number {
  // Coarse: align by gradient ZNCC (warps the precomputed gradient map).
  let bAlign = -1, bDeg = 0, bSc = 1;
  for (const deg of coarseDegs) for (const sc of coarseScales) {
    const t = affineSquare(probe.mag, work, deg, sc, 0, 0);
    const s = znccMasked(t, ref.mag, ref.mask);
    if (s > bAlign) { bAlign = s; bDeg = deg; bSc = sc; }
  }
  // Fine: pick the transform maximising shape score, then add colour there.
  const fineDegs = [bDeg - 3, bDeg, bDeg + 3];
  const fineScales = [bSc * 0.9, bSc, bSc * 1.1];
  const shifts = [-2, 0, 2];
  let bShape = -1, fDeg = bDeg, fSc = bSc, fdx = 0, fdy = 0;
  for (const deg of fineDegs) for (const sc of fineScales)
    for (const dx of shifts) for (const dy of shifts) {
      const wg = affineSquare(probe.gray, work, deg, sc, dx, dy);
      const wm = sobelMag(wg, work, work);
      const shape = W_GRAD * znccMasked(wm, ref.mag, ref.mask)
        + W_GRAY * znccMasked(wg, ref.gray, ref.mask);
      if (shape > bShape) { bShape = shape; fDeg = deg; fSc = sc; fdx = dx; fdy = dy; }
    }
  // colour at the winning transform (3 channel warps)
  const wr = affineSquare(probe.r, work, fDeg, fSc, fdx, fdy);
  const wgc = affineSquare(probe.g, work, fDeg, fSc, fdx, fdy);
  const wb = affineSquare(probe.b, work, fDeg, fSc, fdx, fdy);
  const colour = (znccMasked(wr, ref.r, ref.maskCore)
    + znccMasked(wgc, ref.g, ref.maskCore)
    + znccMasked(wb, ref.b, ref.maskCore)) / 3;
  return bShape + W_COLOR * colour;
}

// Full recognition. `probe` carries the raw crop colour planes (pw×ph) that the
// adapter extracted from the user's box; refs are precomputed per item.
export function recognize(
  probe: RGB, pw: number, ph: number,
  refs: RefEntry[],
  opt: RecogOptions = {},
): ScoredCandidate[] {
  const work = opt.work ?? 48;
  const recallK = opt.recallK ?? 64;
  const coarseDegs = opt.coarseDegs ?? [-12, -6, 0, 6, 12];
  const coarseScales = opt.coarseScales ?? [0.8, 1.0, 1.25, 1.55];
  const probeGray = rgbToGray(probe);

  // 1-2. tighten + extract at working resolution (+ low-res copy for recall)
  const bb = tightenBBox(probeGray, pw, ph);
  const g = cropResample(probeGray, pw, ph, bb.x, bb.y, bb.w, bb.h, work);
  const gLo = cropResample(probeGray, pw, ph, bb.x, bb.y, bb.w, bb.h, RECALL_WORK);
  const pr = cropResample(probe.r, pw, ph, bb.x, bb.y, bb.w, bb.h, work);
  const pg = cropResample(probe.g, pw, ph, bb.x, bb.y, bb.w, bb.h, work);
  const pb = cropResample(probe.b, pw, ph, bb.x, bb.y, bb.w, bb.h, work);

  // 3. recall: cheap coarse masked-ZNCC over ALL refs at RECALL_WORK. Same
  // background-agnostic principle as verify (silhouette mask) but low res and a
  // tiny rot/scale grid — strong recall without pHash's background fragility.
  const probeMagLo = sobelMag(gLo, RECALL_WORK, RECALL_WORK);
  const recallDegs = [-12, -5, 0, 5, 12];
  const recallScales = [0.8, 0.95, 1.1, 1.3, 1.55];
  const warpsLo = recallDegs.flatMap((deg) =>
    recallScales.map((sc) => affineSquare(probeMagLo, RECALL_WORK, deg, sc, 0, 0)),
  );
  const N = refs.length;
  const rscore = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    const ref = refs[i]!;
    let best = -1;
    for (const wmag of warpsLo) {
      const s = znccMasked(wmag, ref.magLo, ref.maskLo);
      if (s > best) best = s;
    }
    rscore[i] = best;
  }
  const cand = Array.from({ length: N }, (_, i) => i)
    .sort((a, b) => rscore[b]! - rscore[a]!)
    .slice(0, recallK);

  // 4. verify: masked shape + colour ZNCC, coarse-to-fine over rot/scale/shift
  const probeMag = sobelMag(g, work, work);
  const pbundle = { r: pr, g: pg, b: pb, gray: g, mag: probeMag };
  const scored: ScoredCandidate[] = cand.map((idx) => ({
    idx,
    score: verifyOne(pbundle, work, refs[idx]!, coarseDegs, coarseScales),
    recallScore: rscore[idx]!,
  }));
  scored.sort((a, b) => b.score - a.score);
  return scored;
}
